import { useNavigate } from 'react-router';
import { useMediaQuery } from '@mui/material';
import { useContextSelector as useCS } from 'use-context-selector';
import CircularProgress from '@mui/material/CircularProgress';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { getMatchScoreColor } from 'view/pages/profile/components/JobPostsListItem';
import { updateMessengerUrl } from 'view/pages/messenger/helpers/index';
import UnavailableIconImage from 'assets/images/UnavailableIcon.jpg';
import { theme } from 'view/themes/theme';
import * as S from './styled';

export const getTimeAgo = (isoDateString: string): string => {
  const now = new Date();
  const messageDate = new Date(isoDateString);
  const diffInSeconds = Math.floor((now.getTime() - messageDate.getTime()) / 1000);

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(diffInSeconds / 3600);
  const days = Math.floor(diffInSeconds / 86400);

  if (diffInSeconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;

  return messageDate.toLocaleDateString('en-US');
};

const ChatsListMainItem = ({ item, conversationId, unreadMessagesPayload, setIsChatOpen }) => {
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.down('laptop'));
  const { role } = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const avatarLink =
    role?.name === RolesEnum.JOB_SEEKER ? item?.ownerPhotoLink : item?.participantPhotoLink;
  const score =
    role?.name === RolesEnum.JOB_SEEKER ? item?.scoreForJobSeeker : item?.scoreForRecruiter;

  const getTextTitle = () => {
    return role?.name === RolesEnum.JOB_SEEKER ? (
      <S.ChatsListMainItemTextTitle>
        {item?.statusName === 'Active'
          ? `${item?.jobPostTitle} at ${item?.jobPostCompany}`
          : item?.statusName}
      </S.ChatsListMainItemTextTitle>
    ) : (
      <>
        {item?.statusName === 'Active' ? (
          <S.ChatsListMainItemTextTitle>
            {item?.participantJobTitle || ''} <span>${item?.participantName || ''}</span>
          </S.ChatsListMainItemTextTitle>
        ) : (
          <S.ChatsListMainItemTextTitle>{item?.statusName}</S.ChatsListMainItemTextTitle>
        )}
      </>
    );
  };

  return (
    <S.ChatsListMainItem
      key={item.id}
      onClick={() => {
        updateMessengerUrl(item.id, navigate);
        if (matches) setIsChatOpen(true);
      }}
      className={conversationId && conversationId === item.id ? 'selected' : ''}
    >
      <div style={{ width: '48px', height: '48px', position: 'relative' }}>
        <img
          src={item?.statusName === 'Active' ? avatarLink : UnavailableIconImage}
          alt="avatar"
          style={{ width: '48px', height: '48px', borderRadius: '50%' }}
        />

        {score && item?.statusName === 'Active' ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <CircularProgress
              variant="determinate"
              value={score > 100 ? 100 : score}
              style={{
                width: '48px',
                height: '48px',
                color: getMatchScoreColor(score),
              }}
            />
          </div>
        ) : null}
      </div>
      <S.ChatsListMainItemText>
        {getTextTitle()}
        <S.ChatsListMainItemTextLastMsg>
          {item?.lastMessageText || 'No messages yet'}
        </S.ChatsListMainItemTextLastMsg>
      </S.ChatsListMainItemText>
      <S.ChatsListMainItemInfo>
        <S.ChatsListMainItemInfoTime>
          {getTimeAgo(item?.lastMessageDate)}
        </S.ChatsListMainItemInfoTime>
        <S.ChatsListMainItemInfoCountMsgs
          className={unreadMessagesPayload?.[item?.id] ? 'withBackground' : ''}
        >
          {unreadMessagesPayload?.[item?.id] || ''}
        </S.ChatsListMainItemInfoCountMsgs>
      </S.ChatsListMainItemInfo>
    </S.ChatsListMainItem>
  );
};

export default ChatsListMainItem;
